import { clsx } from 'clsx';
import { Loader2 } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'destructive';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  children: React.ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  className,
  children,
  disabled,
  type = 'button',
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;
  return (
    <button
      type={type}
      className={clsx(
        'inline-flex items-center justify-center font-semibold text-sm transition-all duration-300',
        // Size variants
        {
          'px-4 py-2 text-xs': size === 'sm',
          'px-6 py-3': size === 'md',
          'px-8 py-4 text-base': size === 'lg',
        },
        // Style variants (base colors always applied)
        {
          'bg-action text-wolf-900': variant === 'primary',
          'bg-transparent border border-wolf-700 text-wolf-300': variant === 'secondary',
          'bg-transparent text-wolf-300': variant === 'ghost',
          'bg-red-600 text-white': variant === 'destructive',
        },
        // Hover effects (only when not disabled)
        {
          'hover:bg-action-light hover:shadow-[0_0_20px_rgba(132,204,22,0.4)]':
            variant === 'primary' && !isDisabled,
          'hover:border-wolf-500 hover:text-wolf-100':
            variant === 'secondary' && !isDisabled,
          'hover:bg-wolf-900/50 hover:text-wolf-100':
            variant === 'ghost' && !isDisabled,
          'hover:bg-red-700': variant === 'destructive' && !isDisabled,
        },
        // Disabled state
        {
          'opacity-50 cursor-not-allowed': isDisabled,
        },
        // Focus state
        'focus:outline-none focus:ring-2 focus:ring-action focus:ring-offset-2 focus:ring-offset-navy',
        className
      )}
      disabled={isDisabled}
      {...props}
    >
      {loading ? (
        <Loader2
          className={clsx(
            "w-5 h-5 animate-spin",
            variant === 'primary' && "text-wolf-900",
            variant === 'secondary' && "text-wolf-300",
            variant === 'ghost' && "text-wolf-300",
            variant === 'destructive' && "text-white"
          )}
        />
      ) : children}
    </button>
  );
}
